export interface PinterestAttributeOptions {
    articleUrl: string;
    defaultDescription?: string;
}

export interface PinterestScriptOptions {
    hover?: boolean;
    save?: boolean;
}

const IMAGE_TAG_REGEX = /<img\b[^>]*>/gi;
const PINTEREST_SCRIPT_ID = 'pinterest-pinit';
const PINTEREST_SCRIPT_SRC = 'https://assets.pinterest.com/js/pinit.js';

export function addPinterestDataAttributes(
    html: string,
    { articleUrl, defaultDescription = '' }: PinterestAttributeOptions,
): string {
    if (!html || !articleUrl) {
        return html;
    }

    return html.replace(IMAGE_TAG_REGEX, (tag) => {
        const isSelfClosing = tag.endsWith('/>');
        const tagWithoutBrackets = tag.slice(4, tag.length - (isSelfClosing ? 2 : 1));
        const hasPinUrl = /\bdata-pin-url\s*=/.test(tagWithoutBrackets);
        const hasPinMedia = /\bdata-pin-media\s*=/.test(tagWithoutBrackets);
        const hasPinDescription = /\bdata-pin-description\s*=/.test(tagWithoutBrackets);

        if (hasPinUrl && hasPinMedia && hasPinDescription) {
            return tag;
        }

        let workingAttributes = tagWithoutBrackets.trim();

        if (!hasPinUrl) {
            workingAttributes = appendAttribute(
                workingAttributes,
                'data-pin-url',
                articleUrl,
            );
        }

        if (!hasPinMedia) {
            const media = getAttributeValue(tagWithoutBrackets, 'src');
            if (media) {
                workingAttributes = appendAttribute(workingAttributes, 'data-pin-media', media);
            }
        }

        if (!hasPinDescription) {
            const altRaw = getAttributeValue(tagWithoutBrackets, 'alt');
            const alt = altRaw ? decodeHtmlEntities(altRaw).trim() : '';
            const description = alt || defaultDescription;
            if (description) {
                workingAttributes = appendAttribute(workingAttributes, 'data-pin-description', description);
            }
        }

        const rebuilt = workingAttributes.length > 0 ? ` ${workingAttributes}` : '';
        return `<img${rebuilt}${isSelfClosing ? ' />' : '>'}`;
    });
}

function appendAttribute(existing: string, name: string, value: string): string {
    const escapedValue = escapeAttributeValue(value);
    return [existing, `${name}="${escapedValue}"`].filter(Boolean).join(existing ? ' ' : '');
}

function getAttributeValue(attributes: string, name: string): string | undefined {
    const regex = new RegExp(`\\b${name}\\s*=\\s*(?:"([^"]*)"|'([^']*)'|([^\\s>]+))`, 'i');
    const match = attributes.match(regex);
    if (!match) {
        return undefined;
    }

    return match[1] ?? match[2] ?? match[3] ?? '';
}

function decodeHtmlEntities(value: string): string {
    return value
        .replace(/&quot;/g, '"')
        .replace(/&#39;/g, "'")
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/&amp;/g, '&');
}

function escapeAttributeValue(value: string): string {
    return value
        .replace(/&/g, '&amp;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');
}

let pinterestScriptPromise: Promise<void> | null = null;

export function ensurePinterestScriptLoaded(options: PinterestScriptOptions = {}): Promise<void> {
    if (typeof window === 'undefined') {
        return Promise.resolve();
    }

    if (window.PinUtils?.build) {
        return Promise.resolve();
    }

    if (pinterestScriptPromise) {
        if (options.hover) {
            document
                .querySelector<HTMLScriptElement>(`script#${PINTEREST_SCRIPT_ID}`)
                ?.setAttribute('data-pin-hover', 'true');
        }
        if (options.save) {
            document
                .querySelector<HTMLScriptElement>(`script#${PINTEREST_SCRIPT_ID}`)
                ?.setAttribute('data-pin-save', 'true');
        }
        return pinterestScriptPromise;
    }

    pinterestScriptPromise = new Promise((resolve, reject) => {
        const existing = document.querySelector<HTMLScriptElement>(`script#${PINTEREST_SCRIPT_ID}`);
        const script = existing ?? document.createElement('script');

        const cleanup = () => {
            script.removeEventListener('load', handleLoad);
            script.removeEventListener('error', handleError);
            pinterestScriptPromise = null;
        };

        const handleLoad = () => {
            script.dataset.pinterestLoaded = 'true';
            cleanup();
            resolve();
        };

        const handleError = () => {
            cleanup();
            reject(new Error('Failed to load Pinterest script'));
        };

        script.addEventListener('load', handleLoad);
        script.addEventListener('error', handleError);

        if (!existing) {
            script.id = PINTEREST_SCRIPT_ID;
            script.src = PINTEREST_SCRIPT_SRC;
            script.async = true;
            script.defer = true;
            script.dataset.pinterestScript = 'true';
            if (options.hover) {
                script.setAttribute('data-pin-hover', 'true');
            }
            if (options.save) {
                script.setAttribute('data-pin-save', 'true');
            }
            document.head.appendChild(script);
        } else if (script.dataset.pinterestLoaded === 'true' || window.PinUtils) {
            if (options.hover) {
                script.setAttribute('data-pin-hover', 'true');
            }
            if (options.save) {
                script.setAttribute('data-pin-save', 'true');
            }
            handleLoad();
        } else if ((script as unknown as { readyState?: string }).readyState === 'complete') {
            if (options.hover) {
                script.setAttribute('data-pin-hover', 'true');
            }
            if (options.save) {
                script.setAttribute('data-pin-save', 'true');
            }
            handleLoad();
        } else {
            if (options.hover) {
                script.setAttribute('data-pin-hover', 'true');
            }
            if (options.save) {
                script.setAttribute('data-pin-save', 'true');
            }
        }
    });

    return pinterestScriptPromise;
}

declare global {
    interface Window {
        PinUtils?: {
            build?: () => void;
        };
    }
}

export {};
